/**
 * optionExpiry — shared option expiry/settlement formatting for the option UIs.
 *
 * Delta options settle 12:00 UTC (17:30 IST); NSE/NFO at 10:00 UTC (15:30 IST).
 * Previously this was duplicated in BasketReviewDialog and StrategyArchitect.
 */

export const settlementHourIst = (feedSource?: string): number =>
  String(feedSource).toUpperCase() === 'DELTA' ? 17 : 15; // wall-clock IST

/** Settlement instant (epoch ms) for an ISO date. IST = UTC+5:30. */
export const settlementMs = (expiryIso?: string, feedSource?: string): number | null => {
  if (!expiryIso) return null;
  const [y, mo, d] = String(expiryIso).split('T')[0].split('-').map(Number);
  if (!y || !mo || !d) return null;
  return Date.UTC(y, mo - 1, d, settlementHourIst(feedSource) - 5, 0, 0); // 17:30/15:30 IST → 12:00/10:00 UTC
};

/** Friendly expiry stamp (IST) + time-to-go. */
export const expiryClock = (expiry?: string, feedSource?: string): { label: string; dte: string; expired: boolean } | null => {
  const utcMs = settlementMs(expiry, feedSource);
  if (utcMs == null) return null;
  const label = new Date(utcMs).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', weekday: 'short', day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const diffMs = utcMs - Date.now();
  if (diffMs <= 0) return { label: `${label} IST`, dte: 'expired', expired: true };
  const totalH = diffMs / 3.6e6, days = Math.floor(totalH / 24), hours = Math.floor(totalH % 24);
  return { label: `${label} IST`, dte: days > 0 ? `${days}d ${hours}h` : `${hours}h`, expired: false };
};

/** Price-display currency symbol by feed (Delta quotes USD, NSE INR). */
export const priceCcy = (feedSource?: string): '$' | '₹' =>
  String(feedSource).toUpperCase() === 'DELTA' ? '$' : '₹';
